import { cva, type VariantProps } from 'class-variance-authority';

// Discuss: do we want to make a cva where you can pass mobile and desktop values?
// Fe: PaddingY({mobile: md, desktop: lg})
export enum PaddingY {
    xs = 'py-1', // 4
    sm = 'py-2', // 8
    md = 'py-4', // 16
    lg = 'py-8', // 32
    xl = 'py-10', // 40
    '2xl' = 'py-10 lg:py-[3.75rem]', // 60
    '3xl' = 'py-[3.75rem] lg:py-[6.25rem]', // 100
    '4xl' = 'py-30', // 120
    '5xl' = 'py-17 lg:py-35', // 140
    '6xl' = 'py-40', // 160
}

export const spacingVariant = cva('', {
    variants: {
        spacing: {
            xs: 'my-1', // 4
            sm: 'my-2', // 8
            md: 'my-4', // 16
            lg: 'my-8', // 32
            xl: 'my-10', // 40
            '2xl': 'my-[3.75rem]', // 60
            '3xl': 'my-[6.25rem]', // 100
            '4xl': 'my-30', // 120
            '5xl': 'my-35', // 140
            '6xl': 'my-40', // 160
        } as const,
    },
});

export const marginBottomVariant = cva('', {
    variants: {
        spacing: {
            xs: 'mb-1',
            sm: 'mb-2',
            md: 'mb-4',
            lg: 'mb-5 lg:mb-8',
            xl: 'mb-8 lg:mb-10',
            '2xl': 'mb-10 lg:mb-[3.75rem]',
            '3xl': 'mb-[6.25rem]',
            '4xl': 'mb-30',
            '5xl': 'mb-35',
            '6xl': 'mb-40',
        } as const,
    },
});

export const backgroundColorVariant = cva('', {
    variants: {
        backgroundColor: {
            white: 'bg-white',
            black: 'bg-black',
            'brown-50': 'bg-brown-50',
            'brown-100': 'bg-brown-100',
            primary: 'bg-primary',
            secondary: 'bg-secondary',
            tertiary: 'bg-tertiary',
            'blue-900': 'bg-blue-900',
            'blue-950': 'bg-blue-950',
        } as const,
    },
});
export type BackgroundColorVariant = NonNullable<
    VariantProps<typeof backgroundColorVariant>['backgroundColor']
>;

/** Storyblok hands its colour field over as a plain string, so it has to be
 * checked before it reaches the variant. Written as a map rather than a list so
 * a colour added above fails to compile until it is named here too. */
const BACKGROUND_COLOR_VARIANTS: Record<BackgroundColorVariant, true> = {
    white: true,
    black: true,
    'brown-50': true,
    'brown-100': true,
    primary: true,
    secondary: true,
    tertiary: true,
    'blue-900': true,
    'blue-950': true,
};

export function isBackgroundColorVariant(value: unknown): value is BackgroundColorVariant {
    return typeof value === 'string' && Object.hasOwn(BACKGROUND_COLOR_VARIANTS, value);
}

export const textColorVariant = cva('', {
    variants: {
        textColor: {
            white: 'text-white',
            black: 'text-black',
            'brown-50': 'text-brown-50',
            'brown-100': 'text-brown-100',
            primary: 'text-primary',
            secondary: 'text-secondary',
            tertiary: 'text-tertiary',
            'blue-900': 'text-primary',
            'blue-950': 'text-blue-950',
        } as const,
    },
});

export const alignmentVariant = cva('', {
    variants: {
        alignment: {
            left: 'text-left',
            center: 'text-center',
            right: 'text-right',
        } as const,
    },
});
